import { apiClient } from '../lib/apiClient';
import type { AppTheme, AppColors, ReportColors } from '../types/theme';

const THEME_STORAGE_KEY = 'app_active_theme_slug';

export async function fetchAllThemes(): Promise<AppTheme[]> {
  return apiClient.get<AppTheme[]>('/api/themes');
}

export async function fetchActiveTheme(): Promise<AppTheme | null> {
  const themes = await apiClient.get<AppTheme[]>('/api/themes');
  return themes.find((t) => t.is_active) ?? null;
}

/**
 * The theme to actually render for a user: their personal choice if it's set and
 * still exists, otherwise the org default (is_active). This is what the app loads
 * on startup — the global `is_active` theme is only the fallback default.
 */
export async function fetchEffectiveTheme(userThemeSlug?: string | null): Promise<AppTheme | null> {
  const themes = await apiClient.get<AppTheme[]>('/api/themes');
  if (userThemeSlug) {
    const mine = themes.find((t) => t.slug === userThemeSlug);
    if (mine) return mine;
  }
  return themes.find((t) => t.is_active) ?? null;
}

/** Persist the current user's personal theme choice (null clears it → org default). */
export async function setMyTheme(slug: string | null): Promise<void> {
  await apiClient.put('/api/auth/me', { preferredThemeSlug: slug });
}

export async function activateTheme(themeId: string): Promise<void> {
  await apiClient.post<AppTheme>(`/api/themes/${themeId}/activate`);
}

export async function createTheme(theme: {
  name: string;
  slug: string;
  app_colors: AppColors;
  report_colors: ReportColors;
}): Promise<AppTheme> {
  return apiClient.post<AppTheme>('/api/themes', {
    name: theme.name,
    slug: theme.slug,
    is_builtin: false,
    is_active: false,
    app_colors: theme.app_colors,
    report_colors: theme.report_colors,
  });
}

export async function updateTheme(
  id: string,
  updates: Partial<Pick<AppTheme, 'name' | 'app_colors' | 'report_colors'>>,
): Promise<void> {
  await apiClient.put<AppTheme>(`/api/themes/${id}`, updates);
}

export async function deleteTheme(id: string): Promise<void> {
  await apiClient.del(`/api/themes/${id}`);
}

/**
 * Perceived-luminance test so we can tell whether the active theme (which may be
 * a built-in Dark/Greyscale or an admin's custom theme) is dark. Used only to
 * flip a `data-theme-mode` signal on <html>; not a color computation.
 * Accepts #rgb / #rrggbb / rgb()/rgba(). Falls back to "light" on unparseable input.
 */
function isThemeDark(color: string | undefined): boolean {
  if (!color) return false;
  const c = color.trim();
  let r: number, g: number, b: number;
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
    if (full.length < 6) return false;
    r = parseInt(full.slice(0, 2), 16);
    g = parseInt(full.slice(2, 4), 16);
    b = parseInt(full.slice(4, 6), 16);
  } else {
    const m = c.match(/rgba?\(([^)]+)\)/i);
    if (!m) return false;
    const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
    [r, g, b] = parts;
  }
  if ([r, g, b].some((n) => Number.isNaN(n))) return false;
  // Rec. 601 luma, 0..1. Below ~0.5 reads as a dark surface.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

export function applyThemeToDOM(appColors: AppColors): void {
  const root = document.documentElement;

  // Expose a coarse light/dark flag so Tailwind `dark:` variants can restyle the
  // intentionally-colored callouts/badges (alerts, status pills) for dark themes.
  // Derived from the page background so it works for any custom theme too.
  root.setAttribute('data-theme-mode', isThemeDark(appColors.page_bg_color) ? 'dark' : 'light');

  root.style.setProperty('--color-primary', appColors.primary_color);
  root.style.setProperty('--color-secondary', appColors.secondary_color);
  root.style.setProperty('--color-header-bg', appColors.header_bg_color);
  root.style.setProperty('--color-header-text', appColors.header_text_color);
  root.style.setProperty('--color-menu-bg', appColors.menu_bg_color);
  root.style.setProperty('--color-menu-text', appColors.menu_text_color);
  root.style.setProperty('--color-footer-bg', appColors.footer_bg_color);
  root.style.setProperty('--color-footer-text', appColors.footer_text_color);
  root.style.setProperty('--color-page-bg', appColors.page_bg_color);
  root.style.setProperty('--color-card-bg', appColors.card_bg_color);
  root.style.setProperty('--color-text-primary', appColors.text_primary_color);
  root.style.setProperty('--color-text-secondary', appColors.text_secondary_color);
  root.style.setProperty('--color-border', appColors.border_color);
  root.style.setProperty('--color-input-bg', appColors.input_bg_color);
  root.style.setProperty('--color-input-border', appColors.input_border_color);
  root.style.setProperty('--color-hover-bg', appColors.hover_bg_color);
  root.style.setProperty('--color-active-item-bg', appColors.active_item_bg);
  root.style.setProperty('--color-active-item-text', appColors.active_item_text);
}

export function getStoredThemeSlug(): string | null {
  return localStorage.getItem(THEME_STORAGE_KEY);
}

export function storeThemeSlug(slug: string): void {
  localStorage.setItem(THEME_STORAGE_KEY, slug);
}
